import React, { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Download, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { trpcClient } from "@/lib/trpc";
import { formatMoney, formatDate } from "@/lib/utils";
import { DatePickerWithRange } from "@/components/ui/date-range-picker";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { Link } from "@tanstack/react-router";
import EntityLink from "@/components/shared/EntityLink";
import PrintDialog from "@/components/shared/PrintDialog";
import DebtReconciliationPrintTemplate from "@/components/finance/print/DebtReconciliationPrintTemplate";
import { toVnDateString } from "@apv-erp/status";

export default function ARLedgerView() {
  const { id: customerId } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<any>();
  const [isPrintOpen, setIsPrintOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["ar-ledger", customerId, dateRange],
    queryFn: () =>
      trpcClient.ar.getLedger.query({
        customerId,
        fromDate: dateRange?.from ? toVnDateString(dateRange.from) : undefined,
        toDate: dateRange?.to ? toVnDateString(dateRange.to) : undefined,
      }),
    enabled: !!customerId,
  });

  const ledgerData = data?.ledger || [];
  const customer = data?.customer;
  const openingBalance = data?.openingBalance || 0;
  const closingBalance = data?.closingBalance || 0;

  return (
    <StandardDetailLayout
      title="Sổ chi tiết công nợ"
      // Ghi chú / đính kèm / lịch sử của sổ công nợ đối tác; thời gian = phát sinh đầu tiên / mới nhất trong kỳ
      entityType="ar_ledger"
      entityId={customerId}
      timestamps={{
        // chưa có phát sinh: sổ mở cùng hồ sơ đối tác
        created: ledgerData.length ? ledgerData[0]?.date : customer?.createdAt,
        updated: ledgerData.length ? ledgerData[ledgerData.length - 1]?.date : customer?.updatedAt || customer?.createdAt,
        createdBy: ledgerData[0]?.refId ? `chứng từ ${ledgerData[0].refId}` : "Chưa có phát sinh",
        updatedBy: ledgerData[ledgerData.length - 1]?.refId ? `chứng từ ${ledgerData[ledgerData.length - 1].refId}` : "Chưa có phát sinh",
      }}
      breadcrumbs={[
        { label: "Công nợ phải thu", to: "/finance/ar" },
        { label: customer?.code || "Chi tiết" },
      ]}
      actions={
        <div className="flex items-center gap-2">
          <DatePickerWithRange value={dateRange} onChange={setDateRange} />
          <Button variant="outline" onClick={() => setIsPrintOpen(true)}>
            <Printer className="w-4 h-4 mr-2" />
            In sổ
          </Button>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Xuất Excel
          </Button>
        </div>
      }
    >
      <div className="w-full px-4 sm:px-6 py-4 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                Dư nợ đầu kỳ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`text-2xl font-bold ${openingBalance > 0 ? "text-rose-600" : "text-emerald-600"}`}
              >
                {formatMoney(openingBalance)}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                Phát sinh trong kỳ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Ghi nợ (Bán hàng):</span>
                  <span className="font-semibold text-rose-600">
                    {formatMoney(
                      ledgerData.reduce(
                        (sum: number, item: any) => sum + item.debit,
                        0,
                      ),
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Ghi có (Thu tiền):</span>
                  <span className="font-semibold text-emerald-600">
                    {formatMoney(
                      ledgerData.reduce(
                        (sum: number, item: any) => sum + item.credit,
                        0,
                      ),
                    )}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-500">
                Dư nợ cuối kỳ
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`text-2xl font-bold ${closingBalance > 0 ? "text-rose-600" : "text-emerald-600"}`}
              >
                {formatMoney(closingBalance)}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <div className="overflow-x-auto min-h-[40vh]">
            <table className="w-full text-sm caption-bottom">
              <thead>
                <tr className="border-b bg-slate-50/50">
                  <th className="px-4 py-3 text-center font-medium text-slate-500 w-[100px]">
                    Ngày
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-500 w-[150px]">
                    Số chứng từ
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-slate-500 min-w-[200px]">
                    Diễn giải
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-slate-500 w-[150px]">
                    Ghi nợ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-slate-500 w-[150px]">
                    Ghi có
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-slate-500 w-[150px]">
                    Số dư
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  Array(5)
                    .fill(0)
                    .map((_, i) => (
                      <tr key={i}>
                        <td colSpan={6} className="p-4">
                          <div className="h-6 bg-slate-100 rounded animate-pulse" />
                        </td>
                      </tr>
                    ))
                ) : ledgerData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center py-12 text-slate-400"
                    >
                      Không có phát sinh trong kỳ
                    </td>
                  </tr>
                ) : (
                  <>
                    <tr className="bg-slate-50/30">
                      <td className="px-4 py-3 text-center font-medium text-slate-500">
                        -
                      </td>
                      <td className="px-4 py-3 text-left font-medium text-slate-500">
                        -
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-700">
                        Dư đầu kỳ
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-slate-400">
                        -
                      </td>
                      <td className="px-4 py-3 text-right font-medium text-slate-400">
                        -
                      </td>
                      <td
                        className={`px-4 py-3 text-right font-bold ${openingBalance > 0 ? "text-rose-600" : "text-slate-600"}`}
                      >
                        {formatMoney(openingBalance)}
                      </td>
                    </tr>
                    {ledgerData.map((item: any, idx: number) => (
                      <tr
                        key={idx}
                        className="hover:bg-slate-50 transition-colors"
                      >
                        <td className="px-4 py-3 text-center text-slate-600">
                          {formatDate(item.date)}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-900">
                          {(() => {
                            if (!item.refId) return "—";
                            const ref = String(item.refId);
                            const isDelivery = item.type === "DELIVERY" || item.type === "INVOICE" || ref.startsWith("XK") || ref.startsWith("DEL") || ref.startsWith("GH");
                            const isReceipt = item.type === "RECEIPT" || ref.startsWith("PT") || ref.startsWith("RCP");
                            const isOrder = item.type === "ORDER" || ref.startsWith("DH") || ref.startsWith("PO");

                            if (isDelivery) {
                              return <EntityLink entityType="delivery" id={ref} code={ref} />;
                            }
                            if (isReceipt) {
                              return <EntityLink entityType="receipt" id={ref} code={ref} />;
                            }
                            if (isOrder) {
                              return <EntityLink entityType="order" id={ref} code={ref} />;
                            }
                            return ref;
                          })()}
                        </td>
                        <td className="px-4 py-3 text-slate-600">
                          {item.description}
                        </td>
                        <td className="px-4 py-3 text-right text-rose-600 font-medium">
                          {item.debit > 0 ? formatMoney(item.debit) : "-"}
                        </td>
                        <td className="px-4 py-3 text-right text-emerald-600 font-medium">
                          {item.credit > 0 ? formatMoney(item.credit) : "-"}
                        </td>
                        <td
                          className={`px-4 py-3 text-right font-bold ${item.balance > 0 ? "text-rose-600" : "text-emerald-600"}`}
                        >
                          {formatMoney(item.balance)}
                        </td>
                      </tr>
                    ))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {isPrintOpen && (
        <PrintDialog
          open={isPrintOpen}
          onClose={() => setIsPrintOpen(false)}
          type="custom"
          title="In Sổ Chi Tiết Công Nợ Phải Thu"
        >
          <DebtReconciliationPrintTemplate
            data={{
              reconciliationCode: `SCT-${customer?.code || customerId}`,
              month: (dateRange?.to ? new Date(dateRange.to) : new Date()).getMonth() + 1,
              year: (dateRange?.to ? new Date(dateRange.to) : new Date()).getFullYear(),
              customerName: customer?.name || "",
              customerCode: customer?.code,
              customerAddress: customer?.address,
              customerTaxCode: customer?.taxCode,
              contactPerson: customer?.contactPerson,
              phone: customer?.phone,
              openingBalance,
              totalIncurred: ledgerData.reduce((s: number, i: any) => s + Number(i.debit || 0), 0),
              totalPaid: ledgerData.reduce((s: number, i: any) => s + Number(i.credit || 0), 0),
              closingBalance,
              deliveries: ledgerData
                .filter((i: any) => Number(i.debit) > 0)
                .map((i: any) => ({ date: i.date, docNo: String(i.refId || ""), productName: i.description || "", quantity: 0, amount: Number(i.debit) })),
              payments: ledgerData
                .filter((i: any) => Number(i.credit) > 0)
                .map((i: any) => ({ date: i.date, receiptNo: String(i.refId || ""), method: i.type || "", amount: Number(i.credit), notes: i.description })),
            }}
          />
        </PrintDialog>
      )}
    </StandardDetailLayout>
  );
}
